/**
 * DesktopRecon · iter303ak — Quote Sent · Task 1 (Free Estimate path)
 * ────────────────────────────────────────────────────────────────────
 * Stripped-down desktop appraisal for Free Estimate variants. Provides
 * Google Earth + Street View deep-links and captures three notes fields
 * (Google Earth observations, Street View observations, drive-by notes)
 * before the operator can advance. All three notes are optional but at
 * least one must be filled in.
 *
 * Writes to captured_data.appraisal.desktop_recon = {
 *   google_earth_notes, street_view_notes, drive_by_notes,
 *   captured_at, captured_by
 * }
 *
 * Also PATCHes /appraisals/{id} with desktop_recon so the value persists
 * on the appraisal record (Accepted status pre-fills use it).
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Globe2, Map, Car, CheckCircle2, ExternalLink, AlertCircle } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const buildAddress = (prop) => {
  if (!prop) return '';
  const parts = [
    prop.address_num, prop.address_street, prop.address_suburb, prop.address_city,
  ].filter(Boolean);
  return parts.join(' ');
};

export default function DesktopRecon({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const property = capturedData?.property_record || {};
  const address = buildAddress(property);
  const appraisalId = capturedData?.appraisal_id;

  const initial = value || capturedData?.appraisal?.desktop_recon || {};
  const [notes, setNotes] = useState({
    google_earth_notes: initial.google_earth_notes || '',
    street_view_notes: initial.street_view_notes || '',
    drive_by_notes: initial.drive_by_notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (onChange) onChange(notes);
  }, [notes, onChange]);

  const earthUrl = address
    ? `https://earth.google.com/web/search/${encodeURIComponent(address)}`
    : 'https://earth.google.com/';
  const streetUrl = address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
    : 'https://maps.google.com/';

  const oneFilledIn = (notes.google_earth_notes || notes.street_view_notes || notes.drive_by_notes).trim().length > 0;

  const advance = async () => {
    if (!oneFilledIn) { setErr('Capture at least one of the three observation notes before continuing.'); return; }
    setSaving(true); setErr('');
    try {
      if (appraisalId) {
        await api.patch(`/appraisals/${appraisalId}`, {
          desktop_recon: {
            ...notes,
            captured_at: new Date().toISOString(),
          },
        });
      }
      if (onChange) onChange(notes);
      onNext();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="wf-desktop-recon">
      <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-4">
        <div className="flex items-start gap-3">
          <Globe2 className="w-5 h-5 text-sky-700 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-semibold text-sky-900">Free Estimate · Desktop Recon</div>
            <div className="text-sm text-sky-800 mt-1">{params.prompt || 'Verify the roof via Google Earth and Street View. Capture your observations below — at least one notes field is required.'}</div>
            <div className="text-xs text-sky-700 mt-2">Property: <span className="font-medium">{address || '(no address on file)'}</span></div>
          </div>
        </div>
      </div>

      {/* Google Earth */}
      <div className="rounded-lg border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
            <Globe2 className="w-4 h-4 text-emerald-600" /> Google Earth (aerial)
          </div>
          <a href={earthUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-700 hover:text-sky-900 inline-flex items-center gap-1" data-testid="wf-desktop-earth-link">
            Open in Google Earth <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <textarea
          className="w-full min-h-[80px] px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
          placeholder="What can you see from above? Roof shape, obvious defects, tree overhang, obstructions…"
          value={notes.google_earth_notes}
          onChange={(e) => setNotes({ ...notes, google_earth_notes: e.target.value })}
          data-testid="wf-desktop-earth-notes"
        />
      </div>

      {/* Street View */}
      <div className="rounded-lg border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
            <Map className="w-4 h-4 text-indigo-600" /> Street View
          </div>
          <a href={streetUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-700 hover:text-sky-900 inline-flex items-center gap-1" data-testid="wf-desktop-street-link">
            Open in Google Maps <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <textarea
          className="w-full min-h-[80px] px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
          placeholder="Roof material / brand / colour / storeys observed from street level. Note any visible defects."
          value={notes.street_view_notes}
          onChange={(e) => setNotes({ ...notes, street_view_notes: e.target.value })}
          data-testid="wf-desktop-street-notes"
        />
      </div>

      {/* Drive-by */}
      <div className="rounded-lg border border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-2 text-sm font-medium text-slate-800">
          <Car className="w-4 h-4 text-amber-600" /> Drive-by observations (optional)
        </div>
        <textarea
          className="w-full min-h-[70px] px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
          placeholder="If you drove past — anything else worth noting? Access, safety, adjacent buildings, potential quote types…"
          value={notes.drive_by_notes}
          onChange={(e) => setNotes({ ...notes, drive_by_notes: e.target.value })}
          data-testid="wf-desktop-driveby-notes"
        />
      </div>

      {err ? (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3">
          <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-desktop-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel ? (
            <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-desktop-cancel">Cancel</button>
          ) : null}
          <button
            onClick={advance}
            disabled={saving || !oneFilledIn}
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            data-testid="wf-desktop-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> {saving ? 'Saving…' : 'Save Recon & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
